import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SocialPost, SocialUser, SocialRoom, SocialNotification, SocialComment, AppNotification } from '../types';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { accountGeneration, accountStorage, scopeToAccount } from '../lib/accountStorage';
import { awardSocialXP } from '../utils/socialXP';
import { invalidateActivePactsCache } from '../utils/pactAutoCheckIn';
import { pactSideOf, canNudgeSide, isSameCadencePeriod } from '../utils/pactDirection';
import { parseCustomSchedule, type PactCustomSchedule } from '../utils/pactSchedule';
import { parseActionRef, parseActionPayload, type PactActionRef, type PactActionPayload } from '../utils/pactActionRef';
import { useCurrentView } from '../hooks/useCurrentView';
import { useGiftStore } from './giftStore';
import type { CoinGiftRow } from '../utils/coinGift';
import {
  FEED_COLUMNS,
  NOTIFICATION_PAGE_SIZE,
  UNREAD_FETCH_LIMIT,
  appFromRow,
  olderThanCursor,
  socialFromRow,
  type FeedCursor,
  type FeedRow,
  type SenderProfile,
} from '../utils/notificationFeed';

export interface Pact {
  id: string;
  creatorId: string;
  partnerId: string;
  pactType: 'mutual' | 'one_way' | 'mentorship';
  actionType: 'habit' | 'quest' | 'routine' | 'custom';
  habitId?: string;
  questId?: string;
  routineId?: string;
  /** Per-side action refs (②e P3, source of truth; legacy ids above kept). */
  creatorRef?: PactActionRef | null;
  partnerRef?: PactActionRef | null;
  /** C2-sanitized creator action — lets the receiver copy it while mapping. */
  creatorActionPayload?: PactActionPayload | null;
  customActionName?: string;
  customActionDescription?: string;
  cadence: 'daily' | 'weekly' | 'custom';
  /** Specific-days config for cadence 'custom' (habit-schedule shape, §0.12). */
  customSchedule?: PactCustomSchedule | null;
  durationDays: number;
  currentStreak: number;
  longestStreak: number;
  totalCheckIns: number;
  expectedCheckIns: number;
  completionPercentage: number;
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  creatorAccepted: boolean;
  partnerAccepted: boolean;
  /** Per-side archive flags — archiving hides the pact from YOUR lists only. */
  creatorArchived: boolean;
  partnerArchived: boolean;
  acceptedAt?: string;
  completedAt?: string;
  totalXpEarned: number;
  createdAt: string;
  startsAt?: string;
  endsAt?: string;
}

/** One check-in row in a pact's progress feed (either party's). */
export interface PactCheckIn {
  id: string;
  pactId: string;
  userId: string;
  checkInDate: string;
  notes: string | null;
  createdAt: string;
  /** Two-step profile (check-ins FK auth.users — no embeds). */
  author: SocialProfileRef | null;
  /** All encouragement reactions on this check-in. */
  encouragements: { userId: string; emoji: string }[];
  /** The current viewer's reaction, if any. */
  myEncouragement: string | null;
}

/** A "Win" story — 24h shareable accomplishment on the feed rail. */
export interface WinStory {
  id: string;
  author: SocialProfileRef;
  text: string;
  /** Photo stories: the image renders as the card, text overlays as caption. */
  mediaUrl: string | null;
  backgroundColor: string;
  createdAt: string;
  expiresAt: string;
  viewCount: number;
  reactionCount: number;
  viewedByMe: boolean;
  myReaction: string | null;
  isMine: boolean;
}

export interface SocialDomainStats {
  userId: string;
  points: number;
  level: number;
  socialTitle: string;
  socialStreakDays: number;
  lastSocialActivityDate?: string;
  socialActionsCount: number;
}

/** Engagement others gave YOUR content — the Reputation parameter's feed (SO2). */
export interface ReputationStats {
  likes: number;
  reposts: number;
  replies: number;
  /** likes + reposts + replies (equal-weight v1; tunable). */
  total: number;
}

/** One received-engagement event (who liked / reposted / replied to your post). */
export interface ReputationEvent {
  id: string;
  kind: 'like' | 'repost' | 'reply';
  /** The actor who engaged (best-effort — RLS may hide identity → 'Someone'). */
  actorName: string;
  actorAvatar: string | null;
  /** Reply text (kind === 'reply' only). */
  content?: string;
  createdAt: string;
}

// 'guilds' is legacy — the tab moved to the top-level Guilds page; the id stays
// in the union so old persisted state parses, and SocialView remaps it on load.
export type SocialView = 'global' | 'following' | 'people' | 'guilds' | 'chats' | 'marketplace';

/** Minimal profile shape shared with useDirectMessages' DmProfile. */
export interface SocialProfileRef {
  id: string;
  name: string;
  avatar: string | null;
  level: number;
  handle?: string | null;
}

interface SocialState {
  posts: SocialPost[];
  users: SocialUser[];
  rooms: SocialRoom[];
  notifications: SocialNotification[];
  /** Notices from the app (`user_notifications`), merged with `notifications` in the center. */
  appNotifications: AppNotification[];
  /** Where the next page of the merged feed starts; null = nothing loaded yet. */
  notificationsCursor: FeedCursor | null;
  hasMoreNotifications: boolean;
  loadingMoreNotifications: boolean;
  /**
   * EVERY unread notification id → its kind, fetched apart from the pages so the
   * badge is the true count (not "unread among the rows loaded") and can still
   * be filtered by the muted categories.
   */
  unreadNotifications: Record<string, string>;
  following: string[];
  stories: WinStory[];
  pacts: Pact[];
  socialStats: SocialDomainStats | null;
  reputationStats: ReputationStats | null;
  /** Per-actor received-engagement history (likes/reposts/replies on my posts). */
  reputationEvents: ReputationEvent[];
  /** Count of times others copied my shared items (feeds Contribution, SO7). */
  copiesReceived: number;
  isLoading: boolean;
  currentView: SocialView;
  /** Profile shown in the People tab (any author chip app-wide opens it). */
  profileUserId: string | null;
  /** One-shot DM deep-link target, consumed by ChatsTab on mount. */
  dmTarget: SocialProfileRef | null;
  /** One-shot post-detail deep-link (notification click), consumed by FeedTab. */
  pendingPostId: string | null;

  setCurrentView: (view: SocialView) => void;
  openProfile: (userId: string) => void;
  openDmThread: (profile: SocialProfileRef) => void;
  consumeDmTarget: () => SocialProfileRef | null;
  openPostDetail: (postId: string) => void;
  consumePendingPostId: () => string | null;
  fetchPosts: (feed?: 'global' | 'following') => Promise<void>;
  addPost: (post: Omit<SocialPost, 'id' | 'author' | 'createdAt' | 'likes' | 'comments' | 'isLiked' | 'isSaved' | 'repostCount' | 'viewCount' | 'isReposted'>) => Promise<void>;
  likePost: (postId: string) => Promise<void>;
  /** Repost/undo the ORIGINAL post (pass the original's id). */
  toggleRepost: (postId: string) => Promise<void>;
  /** Record a unique-viewer impression (deduped per session + DB constraint). */
  recordPostView: (postId: string) => void;
  likeComment: (postId: string, commentId: string) => Promise<void>;
  commentOnPost: (postId: string, content: string, parentCommentId?: string) => Promise<void>;
  savePost: (postId: string) => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
  /** Edit an own post's content (re-syncs #tags, marks it edited). */
  editPost: (postId: string, content: string, tags: string[]) => Promise<void>;
  followUser: (userId: string) => Promise<void>;
  unfollowUser: (userId: string) => Promise<void>;
  createRoom: (room: Omit<SocialRoom, 'id' | 'createdAt' | 'memberCount' | 'isMember'>) => void;
  joinRoom: (roomId: string) => void;
  leaveRoom: (roomId: string) => void;
  /** First page of the merged feed + every unread id (replaces what is loaded). */
  fetchNotifications: () => Promise<void>;
  /** The next page of the merged feed, older than the last row loaded. */
  loadMoreNotifications: () => Promise<void>;
  /**
   * Realtime INSERTs (both tables) for the current user; returns an unsubscribe fn.
   * Pass the caller's `signal`: once aborted, NO channel is created (see below).
   */
  subscribeNotifications: (signal?: AbortSignal) => Promise<() => void>;
  /** Per-item read — opening the center no longer reads everything. */
  markNotificationRead: (source: 'social' | 'app', id: string) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  notifyTemplateCopied: (postId: string, ownerId: string) => Promise<void>;

  fetchStories: () => Promise<void>;
  addStory: (text: string, backgroundColor: string, mediaUrl?: string) => Promise<void>;
  markStoryViewed: (storyId: string) => Promise<void>;
  /** Same emoji again un-reacts; a different emoji replaces (one per story). */
  reactToStory: (storyId: string, emoji: string) => Promise<void>;
  deleteStory: (storyId: string) => Promise<void>;

  fetchPacts: () => Promise<void>;
  /** Live pact INSERT/UPDATE/DELETE for the current user; returns an unsubscribe fn. */
  subscribePacts: () => Promise<() => void>;
  createPact: (pact: Partial<Pact>) => Promise<boolean>;
  /** Accept as the partner; doer-side partners pass their mapped ref (§0.5). */
  acceptPact: (pactId: string, partnerRef?: PactActionRef | null) => Promise<void>;
  /** Write/heal the CALLER's side ref (legacy mapping, name snapshots). */
  setMyPactRef: (pactId: string, ref: PactActionRef) => Promise<void>;
  /** Partner turning a pending pact down (cancel with a 'declined' marker). */
  declinePact: (pactId: string) => Promise<void>;
  checkInPact: (pactId: string, notes?: string) => Promise<void>;
  cancelPact: (pactId: string, reason?: string) => Promise<void>;
  /** Nudge the pact's doer — client-written notification, once per cadence period. */
  nudgePact: (pactId: string) => Promise<'sent' | 'limited' | 'failed'>;
  /** Archive/unarchive the pact on the CALLER's side only. */
  setPactArchived: (pactId: string, archived: boolean) => Promise<void>;
  /** Hard delete — the server policy limits this to no-history pacts. */
  deletePact: (pactId: string) => Promise<boolean>;
  /** Both parties' check-ins, newest first, with encouragements hydrated. */
  fetchPactCheckIns: (pactId: string) => Promise<PactCheckIn[]>;
  /** Same emoji again un-encourages; a different emoji replaces (one per check-in). */
  encourageCheckIn: (checkInId: string, emoji: string, current: string | null) => Promise<void>;

  fetchSocialStats: () => Promise<void>;
  refreshSocialStats: () => Promise<void>;
  fetchReputationStats: () => Promise<void>;
  fetchReputationEvents: () => Promise<void>;
  fetchCopiesReceived: () => Promise<void>;
}

// view impressions already sent this session (DB unique constraint is the
// real dedup; this just avoids re-sending on every render)
const viewedThisSession = new Set<string>();

/** Replace a post's tag links with `tags` (find-or-create each tag). */
async function syncPostTags(postId: string, tags: string[], replace: boolean) {
  if (replace) {
    await supabase.from('social_post_tags').delete().eq('post_id', postId);
  }

  for (const tagName of tags) {
    let tagId: string;

    const { data: existingTag } = await supabase
      .from('social_tags')
      .select('id')
      .eq('name', tagName)
      .maybeSingle();

    if (existingTag) {
      tagId = existingTag.id;
    } else {
      const { data: newTag, error: tagError } = await supabase
        .from('social_tags')
        .insert({
          name: tagName,
          display_name: tagName.charAt(0).toUpperCase() + tagName.slice(1)
        })
        .select('id')
        .single();

      if (tagError) {
        console.error('Error creating tag:', tagError);
        continue;
      }
      tagId = newTag.id;
    }

    await supabase
      .from('social_post_tags')
      .insert({
        post_id: postId,
        tag_id: tagId
      });
  }
}

/**
 * Bumped by every full refetch of the notification feed — a page that was in
 * flight across one is dropped rather than appended to the new list.
 */
let feedGeneration = 0;

/** Makes each notifications subscription's realtime topic unique (see subscribeNotifications). */
let notificationChannelSeq = 0;

/** One page of the merged `notification_feed` view (mig 0284), newest first. */
async function fetchFeedPage(userId: string, cursor: FeedCursor | null) {
  let query = supabase
    .from('notification_feed')
    .select(FEED_COLUMNS)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(NOTIFICATION_PAGE_SIZE);
  if (cursor) query = query.or(olderThanCursor(cursor));

  const { data, error } = await query;
  if (error) throw error;
  const rows = (data ?? []) as unknown as FeedRow[];

  // the view can't embed the sender, so profiles come in one batch
  const senderIds = [...new Set(rows.flatMap(r => (r.source === 'social' && r.sender_id ? [r.sender_id] : [])))];
  const profiles = new Map<string, SenderProfile>();
  if (senderIds.length > 0) {
    const { data: found } = await supabase
      .from('user_profiles')
      .select('id, name, avatar_url')
      .in('id', senderIds);
    for (const p of (found ?? []) as SenderProfile[]) profiles.set(p.id, p);
  }

  const last = rows[rows.length - 1];
  return {
    social: rows.filter(r => r.source === 'social').map(r => socialFromRow(r, profiles.get(r.sender_id ?? ''))),
    // team messages (P3) are app notices too — their kind tells the read path apart
    app: rows.filter(r => r.source === 'app' || r.source === 'team').map(appFromRow),
    cursor: last ? { createdAt: last.created_at, id: last.id } : cursor,
    hasMore: rows.length === NOTIFICATION_PAGE_SIZE,
  };
}

/** Every unread id → kind, for the badge (pages only hold the rows loaded). */
async function fetchUnreadIds(userId: string): Promise<Record<string, string>> {
  const { data, error } = await supabase
    .from('notification_feed')
    .select('id, kind')
    .eq('user_id', userId)
    .eq('read', false)
    .limit(UNREAD_FETCH_LIMIT);
  if (error) throw error;
  return Object.fromEntries(((data ?? []) as { id: string; kind: string }[]).map(r => [r.id, r.kind]));
}

export const useSocialStore = create<SocialState>()(
  persist(
    (set, get) => ({
      posts: [],
      users: [],
      rooms: [],
      notifications: [],
      appNotifications: [],
      notificationsCursor: null,
      hasMoreNotifications: false,
      loadingMoreNotifications: false,
      unreadNotifications: {},
      following: [],
      stories: [],
      pacts: [],
      socialStats: null,
      reputationStats: null,
      reputationEvents: [],
      copiesReceived: 0,
      isLoading: false,
      currentView: 'global',
      profileUserId: null,
      dmTarget: null,
      pendingPostId: null,

      setCurrentView: (view) => set({ currentView: view }),

      // Open a user's STANDALONE profile page (epic ③ D4a) — reachable from
      // any author chip app-wide, not just Connect's People tab. Remembers the
      // app view to return to, and keeps People primed as the in-Connect home.
      openProfile: (userId) => {
        set({ profileUserId: userId, currentView: 'people' });
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('selectedProfileId', userId);
            const cv = useCurrentView.getState();
            if (cv.currentView !== 'profile') {
              localStorage.setItem('profileReturnView', cv.currentView);
            }
            cv.setCurrentView('profile');
          } catch { /* storage/nav unavailable */ }
        }
      },

      openDmThread: (profile) => {
        set({ dmTarget: profile, currentView: 'chats' });
        // navigate to Connect app-wide so the thread is visible even when the
        // Message action is triggered from the standalone profile page
        if (typeof window !== 'undefined') {
          try { useCurrentView.getState().setCurrentView('discover'); } catch { /* nav unavailable */ }
        }
      },

      consumeDmTarget: () => {
        const target = get().dmTarget;
        if (target) set({ dmTarget: null });
        return target;
      },

      openPostDetail: (postId) => {
        const view = get().currentView;
        set({
          pendingPostId: postId,
          // keep the current feed lens if already on a feed view
          currentView: view === 'global' || view === 'following' ? view : 'global',
        });
      },

      consumePendingPostId: () => {
        const postId = get().pendingPostId;
        if (postId) set({ pendingPostId: null });
        return postId;
      },

      /* ⚠️⚠️ PER ACCOUNT (lib/accountStorage). This store saves ALL of itself — the
         notifications and their unread ids, pacts, who you follow, the feed as you see it,
         your social stats — and it was one key for the device: the next account to sign in
         read the last one's. Every load below drops an answer that lands after a change of
         account (the feed it asked for is the LAST account's view of it). */
      fetchPosts: async () => {
        const at = accountGeneration();
        try {
          set({ isLoading: true });

          const { data: postsData, error: postsError } = await supabase
            .rpc('get_posts_with_tags', {
              p_limit: 50,
              p_offset: 0,
              p_visibility: null
            });

          if (postsError) throw postsError;

          const { user } = await syncManager.safeAuthCheck();

          if (user) {
            const { data: followsData } = await supabase
              .from('social_follows')
              .select('following_id')
              .eq('follower_id', user.id);
            if (at !== accountGeneration()) return;
            set({ following: (followsData || []).map(f => f.following_id) });
          }

          if (!postsData || postsData.length === 0) {
            set({ posts: [], isLoading: false });
            return;
          }

          const postIds = postsData.map((p: any) => p.id);

          const [likesRes, savesRes, commentsRes] = await Promise.all([
            user
              ? supabase.from('social_likes').select('post_id').eq('user_id', user.id).in('post_id', postIds)
              : Promise.resolve({ data: [] as { post_id: string }[] }),
            user
              ? supabase.from('social_saved_posts').select('post_id').eq('user_id', user.id).in('post_id', postIds)
              : Promise.resolve({ data: [] as { post_id: string }[] }),
            // select * so parent_comment_id appears once its migration runs
            // (and the feed keeps working before it does)
            supabase
              .from('social_comments')
              .select('*')
              .in('post_id', postIds)
              .order('created_at', { ascending: true }),
          ]);

          const likedIds = new Set((likesRes.data || []).map(l => l.post_id));
          const savedIds = new Set((savesRes.data || []).map(s => s.post_id));
          const commentsData = commentsRes.data || [];

          let likedCommentIds = new Set<string>();
          if (user && commentsData.length > 0) {
            const { data: commentLikes } = await supabase
              .from('social_likes')
              .select('comment_id')
              .eq('user_id', user.id)
              .in('comment_id', commentsData.map(c => c.id));
            likedCommentIds = new Set((commentLikes || []).map(l => l.comment_id));
          }

          // my reposts of the posts in this window (also catches reposts older
          // than the window itself) → originalId -> my repost row id
          const myRepostByOriginal = new Map<string, string>();
          if (user) {
            const { data: myReposts } = await supabase
              .from('social_posts')
              .select('id, repost_of_post_id')
              .eq('user_id', user.id)
              .not('repost_of_post_id', 'is', null)
              .in('repost_of_post_id', postIds);
            for (const r of myReposts || []) {
              myRepostByOriginal.set(r.repost_of_post_id, r.id);
            }
          }

          // originals quoted by reposts in the window but not themselves in it
          const postById = new Map(postsData.map((p: any) => [p.id, p]));
          const missingOriginalIds = [...new Set(
            postsData
              .map((p: any) => p.repost_of_post_id)
              .filter((id: string | null): id is string => !!id && !postById.has(id))
          )];
          let originalsById = new Map<string, any>();
          if (missingOriginalIds.length > 0) {
            const { data: originals } = await supabase
              .from('social_posts')
              .select('id, user_id, content, created_at')
              .in('id', missingOriginalIds);
            originalsById = new Map((originals || []).map(o => [o.id, o]));
          }

          const commentsByPost = new Map<string, typeof commentsData>();
          for (const comment of commentsData) {
            const list = commentsByPost.get(comment.post_id) || [];
            list.push(comment);
            commentsByPost.set(comment.post_id, list);
          }

          const userIds = [...new Set([
            ...postsData.map((p: any) => p.user_id),
            ...commentsData.map(c => c.user_id),
            ...[...originalsById.values()].map(o => o.user_id),
          ])];
          const { data: profilesData } = await supabase
            .from('user_profiles')
            .select('id, name, avatar_url, handle')
            .in('id', userIds);
          if (at !== accountGeneration()) return;

          const profilesMap = new Map(
            (profilesData || []).map(p => [p.id, p])
          );

          const following = get().following;
          const toAuthor = (id: string) => {
            const profile = profilesMap.get(id);
            return {
              id,
              name: profile?.name || 'Unknown User',
              avatar: profile?.avatar_url || null,
              isFollowing: following.includes(id),
              handle: profile?.handle || null
            };
          };

          const toRepostOf = (originalId: string | null) => {
            if (!originalId) return undefined;
            const original = postById.get(originalId) || originalsById.get(originalId);
            if (!original) return undefined; // original deleted/unreadable
            return {
              id: original.id,
              author: toAuthor(original.user_id),
              content: original.content,
              createdAt: original.created_at,
            };
          };

          const transformedPosts: SocialPost[] = postsData.map((post: any) => {
            return {
              id: post.id,
              author: toAuthor(post.user_id),
              content: post.content,
              createdAt: post.created_at,
              likes: post.likes_count || 0,
              isLiked: likedIds.has(post.id),
              isSaved: savedIds.has(post.id),
              repostCount: post.repost_count || 0,
              viewCount: post.view_count || 0,
              isEdited: !!post.edited,
              isReposted: myRepostByOriginal.has(post.id),
              myRepostId: myRepostByOriginal.get(post.id),
              repostOf: toRepostOf(post.repost_of_post_id),
              visibility: post.visibility,
              recipientId: post.recipient_id,
              roomId: post.room_id,
              comments: (commentsByPost.get(post.id) || []).map(comment => ({
                id: comment.id,
                author: toAuthor(comment.user_id),
                content: comment.content,
                createdAt: comment.created_at,
                likes: comment.likes_count || 0,
                isLiked: likedCommentIds.has(comment.id),
                parentCommentId: comment.parent_comment_id || undefined
              })),
              mediaUrl: post.media_url,
              mediaType: post.media_type,
              linkPreview: post.link_url ? {
                url: post.link_url,
                title: post.link_title || post.link_url,
                description: post.link_description,
                image: post.link_image
              } : undefined,
              attachedItem: post.attached_item_type ? {
                type: post.attached_item_type,
                id: post.attached_item_id,
                name: post.attached_item_data?.name || '',
                description: post.attached_item_data?.description,
                stats: post.attached_item_data?.stats,
                payload: post.attached_item_data?.payload || undefined
              } : undefined,
              tags: post.tags || []
            };
          });

          set({ posts: transformedPosts, isLoading: false });
        } catch (error) {
          console.error('Error fetching posts:', error);
          set({ isLoading: false });
        }
      },

      addPost: async (postData) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) throw new Error('User not authenticated');

          const { data: post, error: postError } = await supabase
            .from('social_posts')
            .insert({
              user_id: user.id,
              content: postData.content,
              visibility: postData.visibility,
              recipient_id: postData.recipientId,
              media_url: postData.mediaUrl,
              media_type: postData.mediaType,
              link_url: postData.linkPreview?.url,
              link_title: postData.linkPreview?.title,
              link_description: postData.linkPreview?.description,
              link_image: postData.linkPreview?.image,
              attached_item_type: postData.attachedItem?.type,
              attached_item_id: postData.attachedItem?.id,
              attached_item_data: postData.attachedItem ? {
                name: postData.attachedItem.name,
                description: postData.attachedItem.description,
                stats: postData.attachedItem.stats,
                payload: postData.attachedItem.payload ?? null
              } : null
            })
            .select()
            .single();

          if (postError) throw postError;

          if (postData.tags && postData.tags.length > 0) {
            await syncPostTags(post.id, postData.tags, false);
          }

          const { data: profile } = await supabase
            .from('user_profiles')
            .select('name, avatar_url, handle')
            .eq('id', user.id)
            .maybeSingle();

          const newPost: SocialPost = {
            id: post.id,
            author: {
              id: user.id,
              name: profile?.name || 'Current User',
              avatar: profile?.avatar_url || null,
              isFollowing: false,
              handle: profile?.handle || null
            },
            content: postData.content,
            createdAt: post.created_at,
            likes: 0,
            isLiked: false,
            isSaved: false,
            repostCount: 0,
            viewCount: 0,
            isReposted: false,
            visibility: postData.visibility,
            recipientId: postData.recipientId,
            comments: [],
            mediaUrl: postData.mediaUrl,
            mediaType: postData.mediaType,
            linkPreview: postData.linkPreview,
            attachedItem: postData.attachedItem,
            tags: postData.tags
          };

          set(state => ({
            posts: [newPost, ...state.posts]
          }));

          try {
            await awardSocialXP({ actionType: 'post', postId: post.id });
          } catch (xpError) {
            console.error('Error awarding post XP:', xpError);
          }
        } catch (error) {
          console.error('Error creating post:', error);
          throw error;
        }
      },

      likePost: async (postId) => {
        const post = get().posts.find(p => p.id === postId);
        if (!post) return;

        const isLiking = !post.isLiked;

        set(state => ({
          posts: state.posts.map(p => {
            if (p.id !== postId) return p;

            return {
              ...p,
              isLiked: isLiking,
              likes: p.likes + (isLiking ? 1 : -1)
            };
          })
        }));

        try {
          if (isLiking) {
            const { user } = await syncManager.safeAuthCheck();
            if (!user) return;

            await supabase
              .from('social_likes')
              .insert({
                post_id: postId,
                user_id: user.id
              });

            await awardSocialXP({
              actionType: 'like',
              postId
            });
          } else {
            const { user } = await syncManager.safeAuthCheck();
            if (!user) return;

            await supabase
              .from('social_likes')
              .delete()
              .eq('post_id', postId)
              .eq('user_id', user.id);
          }
        } catch (error) {
          console.error('Error toggling like:', error);
        }
      },

      toggleRepost: async (postId) => {
        const post = get().posts.find(p => p.id === postId);
        if (!post) return;

        const isReposting = !post.isReposted;

        const apply = (reposted: boolean, myRepostId?: string) => set(state => ({
          posts: state.posts.map(p => {
            if (p.id !== postId) return p;
            return {
              ...p,
              isReposted: reposted,
              myRepostId,
              repostCount: Math.max(0, p.repostCount + (reposted ? 1 : -1))
            };
          })
        }));

        apply(isReposting, post.myRepostId);

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          if (isReposting) {
            const { data, error } = await supabase
              .from('social_posts')
              .insert({
                user_id: user.id,
                content: '',
                visibility: 'public',
                repost_of_post_id: postId
              })
              .select('id')
              .single();
            if (error) throw error;

            apply(true, data.id);
            await awardSocialXP({ actionType: 'share', postId });
          } else {
            const { error } = await supabase
              .from('social_posts')
              .delete()
              .eq('user_id', user.id)
              .eq('repost_of_post_id', postId);
            if (error) throw error;

            // drop my repost row from the feed if it's in the window
            set(state => ({
              posts: state.posts.filter(p => !(p.repostOf?.id === postId && p.author.id === user.id))
            }));
          }
        } catch (error) {
          console.error('Error toggling repost:', error);
          apply(!isReposting, post.myRepostId);
        }
      },

      recordPostView: (postId) => {
        if (viewedThisSession.has(postId)) return;
        viewedThisSession.add(postId);

        void (async () => {
          try {
            const { user } = await syncManager.safeAuthCheck();
            if (!user) return;

            await supabase
              .from('social_post_views')
              .upsert(
                { post_id: postId, viewer_id: user.id },
                { onConflict: 'post_id,viewer_id', ignoreDuplicates: true }
              );
          } catch {
            // view counting is best-effort
          }
        })();
      },

      likeComment: async (postId, commentId) => {
        const post = get().posts.find(p => p.id === postId);
        const comment = post?.comments.find(c => c.id === commentId);
        if (!comment) return;

        const isLiking = !comment.isLiked;

        const applyLike = (liked: boolean) => set(state => ({
          posts: state.posts.map(p => {
            if (p.id !== postId) return p;
            return {
              ...p,
              comments: p.comments.map(c => {
                if (c.id !== commentId) return c;
                return {
                  ...c,
                  isLiked: liked,
                  likes: Math.max(0, c.likes + (liked ? 1 : -1))
                };
              })
            };
          })
        }));

        applyLike(isLiking);

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          if (isLiking) {
            const { error } = await supabase
              .from('social_likes')
              .insert({
                comment_id: commentId,
                user_id: user.id
              });
            if (error) throw error;

            await awardSocialXP({ actionType: 'like', commentId });
          } else {
            const { error } = await supabase
              .from('social_likes')
              .delete()
              .eq('comment_id', commentId)
              .eq('user_id', user.id);
            if (error) throw error;
          }
        } catch (error) {
          console.error('Error toggling comment like:', error);
          applyLike(!isLiking);
        }
      },

      commentOnPost: async (postId, content, parentCommentId) => {
        const { user } = await syncManager.safeAuthCheck();
        if (!user) throw new Error('User not authenticated');

        const { data: comment, error } = await supabase
          .from('social_comments')
          .insert({
            post_id: postId,
            user_id: user.id,
            content,
            // only sent for replies, so plain comments keep working until
            // the parent_comment_id migration runs
            ...(parentCommentId ? { parent_comment_id: parentCommentId } : {})
          })
          .select()
          .single();

        if (error) throw error;

        const { data: profile } = await supabase
          .from('user_profiles')
          .select('name, avatar_url, handle')
          .eq('id', user.id)
          .maybeSingle();

        const newComment: SocialComment = {
          id: comment.id,
          author: {
            id: user.id,
            name: profile?.name || 'You',
            avatar: profile?.avatar_url || null,
            isFollowing: false,
            handle: profile?.handle || null
          },
          content: comment.content,
          createdAt: comment.created_at,
          likes: 0,
          isLiked: false,
          parentCommentId: comment.parent_comment_id || undefined
        };

        set(state => ({
          posts: state.posts.map(post => {
            if (post.id !== postId) return post;

            return {
              ...post,
              comments: [...post.comments, newComment]
            };
          })
        }));

        try {
          await awardSocialXP({
            actionType: parentCommentId ? 'reply' : 'comment',
            postId,
            commentId: comment.id
          });
        } catch (xpError) {
          console.error('Error awarding comment XP:', xpError);
        }
      },

      savePost: async (postId) => {
        const post = get().posts.find(p => p.id === postId);
        if (!post) return;

        const isSaving = !post.isSaved;

        set(state => ({
          posts: state.posts.map(p => {
            if (p.id !== postId) return p;

            return {
              ...p,
              isSaved: isSaving
            };
          })
        }));

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          if (isSaving) {
            const { error } = await supabase
              .from('social_saved_posts')
              .insert({
                post_id: postId,
                user_id: user.id
              });
            if (error) throw error;
          } else {
            const { error } = await supabase
              .from('social_saved_posts')
              .delete()
              .eq('post_id', postId)
              .eq('user_id', user.id);
            if (error) throw error;
          }
        } catch (error) {
          console.error('Error toggling save:', error);
          set(state => ({
            posts: state.posts.map(p => {
              if (p.id !== postId) return p;

              return {
                ...p,
                isSaved: !isSaving
              };
            })
          }));
        }
      },

      deletePost: async (postId) => {
        const previous = get().posts;

        // optimistic: drop the post AND any repost rows quoting it (the DB
        // cascades those via repost_of_post_id)
        set(state => ({
          posts: state.posts.filter(post => post.id !== postId && post.repostOf?.id !== postId)
        }));

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) throw new Error('User not authenticated');

          const { error } = await supabase
            .from('social_posts')
            .delete()
            .eq('id', postId)
            .eq('user_id', user.id);
          if (error) throw error;
        } catch (error) {
          console.error('Error deleting post:', error);
          set({ posts: previous });
        }
      },

      editPost: async (postId, content, tags) => {
        const previous = get().posts.find(p => p.id === postId);
        if (!previous) return;

        set(state => ({
          posts: state.posts.map(p =>
            p.id === postId ? { ...p, content, tags, isEdited: true } : p
          )
        }));

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) throw new Error('User not authenticated');

          let { error } = await supabase
            .from('social_posts')
            .update({ content, edited: true })
            .eq('id', postId)
            .eq('user_id', user.id);
          // PGRST204 = column missing from PostgREST's schema cache (42703 =
          // the raw Postgres flavor) — the edited column isn't migrated yet
          if (error && (error.code === 'PGRST204' || error.code === '42703')) {
            ({ error } = await supabase
              .from('social_posts')
              .update({ content })
              .eq('id', postId)
              .eq('user_id', user.id));
          }
          if (error) throw error;

          await syncPostTags(postId, tags, true);
        } catch (error) {
          console.error('Error editing post:', error);
          set(state => ({
            posts: state.posts.map(p => (p.id === postId ? previous : p))
          }));
          throw error;
        }
      },

      followUser: async (userId) => {
        set(state => ({
          following: state.following.includes(userId) ? state.following : [...state.following, userId],
          users: state.users.map(user => {
            if (user.id !== userId) return user;

            return {
              ...user,
              isFollowing: true
            };
          }),
          posts: state.posts.map(post => {
            if (post.author.id !== userId) return post;

            return {
              ...post,
              author: { ...post.author, isFollowing: true }
            };
          })
        }));

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { error } = await supabase
            .from('social_follows')
            .insert({
              follower_id: user.id,
              following_id: userId
            });
          if (error) throw error;

          await awardSocialXP({
            actionType: 'message'
          });
        } catch (error) {
          console.error('Error following user:', error);
        }
      },

      unfollowUser: async (userId) => {
        set(state => ({
          following: state.following.filter(id => id !== userId),
          users: state.users.map(user => {
            if (user.id !== userId) return user;

            return {
              ...user,
              isFollowing: false
            };
          }),
          posts: state.posts.map(post => {
            if (post.author.id !== userId) return post;

            return {
              ...post,
              author: { ...post.author, isFollowing: false }
            };
          })
        }));

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { error } = await supabase
            .from('social_follows')
            .delete()
            .eq('follower_id', user.id)
            .eq('following_id', userId);
          if (error) throw error;
        } catch (error) {
          console.error('Error unfollowing user:', error);
        }
      },

      createRoom: (roomData) => {
        const newRoom: SocialRoom = {
          id: crypto.randomUUID(),
          name: roomData.name,
          description: roomData.description,
          isPrivate: roomData.isPrivate,
          memberCount: 1,
          isMember: true,
          createdAt: new Date().toISOString()
        };

        set(state => ({
          rooms: [...state.rooms, newRoom]
        }));
      },

      joinRoom: (roomId) => {
        set(state => ({
          rooms: state.rooms.map(room => {
            if (room.id !== roomId) return room;

            return {
              ...room,
              memberCount: room.memberCount + 1,
              isMember: true
            };
          })
        }));
      },

      leaveRoom: (roomId) => {
        set(state => ({
          rooms: state.rooms.map(room => {
            if (room.id !== roomId) return room;

            return {
              ...room,
              memberCount: room.memberCount - 1,
              isMember: false
            };
          })
        }));
      },

      fetchNotifications: async () => {
        const generation = ++feedGeneration;
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const [page, unread] = await Promise.all([
            fetchFeedPage(user.id, null),
            fetchUnreadIds(user.id),
          ]);
          if (generation !== feedGeneration || at !== accountGeneration()) return;

          set({
            notifications: page.social,
            appNotifications: page.app,
            notificationsCursor: page.cursor,
            hasMoreNotifications: page.hasMore,
            unreadNotifications: unread,
          });
        } catch (error) {
          console.error('Error fetching notifications:', error);
        }
      },

      loadMoreNotifications: async () => {
        const { notificationsCursor, hasMoreNotifications, loadingMoreNotifications } = get();
        if (!notificationsCursor || !hasMoreNotifications || loadingMoreNotifications) return;
        const generation = feedGeneration;
        const at = accountGeneration();
        set({ loadingMoreNotifications: true });
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const page = await fetchFeedPage(user.id, notificationsCursor);
          // ⚠️ a full refetch while this page was in flight owns the list now —
          // appending would splice an old page onto a new first page.
          if (generation !== feedGeneration || at !== accountGeneration()) return;

          set(state => {
            const socialIds = new Set(state.notifications.map(n => n.id));
            const appIds = new Set(state.appNotifications.map(n => n.id));
            return {
              notifications: [...state.notifications, ...page.social.filter(n => !socialIds.has(n.id))],
              appNotifications: [...state.appNotifications, ...page.app.filter(n => !appIds.has(n.id))],
              notificationsCursor: page.cursor,
              hasMoreNotifications: page.hasMore,
            };
          });
        } catch (error) {
          console.error('Error loading more notifications:', error);
        } finally {
          set({ loadingMoreNotifications: false });
        }
      },

      subscribeNotifications: async (signal) => {
        /* ⚠️⚠️ THE CHANNEL BELONGS TO THE ACCOUNT IT WAS MADE FOR (lib/accountStorage). A
           sign-out does not reload the page, and the header subscribes once: an account
           that changes without it unmounting (a sign-in link opened in this tab) leaves
           this channel running — and an event already on its way (a sender still being
           looked up) lands after any change at all. Every handler drops what reaches it
           once the account has moved, so nothing of the last account's is `set` into
           the next one's notifications, unread badge or gifts. */
        const at = accountGeneration();
        const { user } = await syncManager.safeAuthCheck();
        // ⚠️⚠️ Checked BEFORE a channel exists, never "create, then remove":
        // realtime-js DISCONNECTS the socket when removeChannel leaves no
        // channels, and a manual disconnect never reconnects — the other
        // channels, created moments later, then sit in "joining" forever.
        // StrictMode's create→cancel at startup did exactly that (2026-10-04).
        // The same for an account that changed while this was asked: no channel for it.
        if (!user || signal?.aborted || at !== accountGeneration()) return () => {};
        const moved = () => at !== accountGeneration();
        let joinedOnce = false;

        // ⚠️ A topic of its OWN per call: realtime-js hands back the EXISTING
        // channel for a topic it already has, so two subscribers (StrictMode's
        // double effect, a remount racing its cleanup) shared one channel and
        // the first one's removeChannel silently killed the other's — the
        // center then heard nothing at all (found live, 2026-10-04).
        const channel = supabase
          .channel(`notifications-${user.id}-${++notificationChannelSeq}`)
          .on(
            'postgres_changes',
            { event: 'INSERT', schema: 'public', table: 'social_notifications', filter: `user_id=eq.${user.id}` },
            async (payload) => {
              const n: any = payload.new;
              const { data: sender } = await supabase
                .from('user_profiles')
                .select('id, name, avatar_url')
                .eq('id', n.sender_id)
                .maybeSingle();
              if (moved()) return;

              set(state => state.notifications.some(existing => existing.id === n.id)
                ? state
                : {
                    notifications: [
                      socialFromRow({ ...n, kind: n.type }, (sender as SenderProfile | null) ?? undefined),
                      ...state.notifications,
                    ],
                    unreadNotifications: n.read
                      ? state.unreadNotifications
                      : { ...state.unreadNotifications, [n.id]: n.type },
                  });

              // a pact lifecycle notification means the pacts list just
              // changed under us (the stale-acceptance fix's first layer)
              if (typeof n.type === 'string' && n.type.startsWith('pact_')) {
                invalidateActivePactsCache();
                get().fetchPacts();
              }
            }
          )
          .on(
            'postgres_changes',
            { event: 'INSERT', schema: 'public', table: 'user_notifications', filter: `user_id=eq.${user.id}` },
            (payload) => {
              if (moved()) return;
              const n: any = payload.new;
              const item = appFromRow(n);
              set(state => state.appNotifications.some(existing => existing.id === item.id)
                ? state
                : {
                    appNotifications: [item, ...state.appNotifications],
                    unreadNotifications: item.read
                      ? state.unreadNotifications
                      : { ...state.unreadNotifications, [item.id]: item.kind },
                  });
            }
          )
          // ⚠️ No filter: a message to EVERYONE has no recipient column to match.
          // RLS decides what reaches this subscriber (live, addressed to them,
          // sent after their account existed) — and there is no admin read policy,
          // so an admin hears only their own.
          .on(
            'postgres_changes',
            { event: 'INSERT', schema: 'public', table: 'team_messages' },
            (payload) => {
              if (moved()) return;
              const n = payload.new as { id: string; title: string; body: string; published_at: string };
              const item = appFromRow({
                id: n.id, kind: 'team_message', created_at: n.published_at, title: n.title, body: n.body,
                link: null, ref_id: null, read: false,
              });
              set(state => state.appNotifications.some(existing => existing.id === item.id)
                ? state
                : {
                    appNotifications: [item, ...state.appNotifications],
                    unreadNotifications: item.read
                      ? state.unreadNotifications
                      : { ...state.unreadNotifications, [item.id]: item.kind },
                  });
            }
          )
          // gifts (P4): an offer arriving, or answered on another device
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'coin_gifts', filter: `recipient_id=eq.${user.id}` },
            (payload) => {
              if (payload.eventType === 'DELETE' || moved()) return;
              useGiftStore.getState().upsertRow(payload.new as CoinGiftRow);
            }
          )
          .subscribe((status) => {
            if (status !== 'SUBSCRIBED') return;
            // ⚠️ Realtime does not replay what happened while the socket was
            // down (a hidden tab's throttled heartbeat, a laptop waking up). A
            // RE-join therefore catches up from the table; the first join
            // follows the fetch the caller already made.
            if (joinedOnce) {
              void get().fetchNotifications();
              void useGiftStore.getState().load();
            }
            joinedOnce = true;
          });

        return () => {
          supabase.removeChannel(channel);
        };
      },

      markNotificationRead: async (source, id) => {
        const state = get();
        const wasUnread = id in state.unreadNotifications
          || (source === 'social'
            ? state.notifications.some(n => n.id === id && !n.read)
            : state.appNotifications.some(n => n.id === id && !n.read));
        if (!wasUnread) return;
        // the kind from the loaded row, or from the unread set when it isn't loaded
        const kind = state.appNotifications.find(n => n.id === id)?.kind ?? state.unreadNotifications[id];

        try {
          // ⚠️ inside the try: with storage blocked, persist's write throws AFTER
          // memory is updated — the item still reads as read, and the row below
          // must still be written.
          set(s => {
            const unreadNotifications = { ...s.unreadNotifications };
            delete unreadNotifications[id];
            return source === 'social'
              ? { unreadNotifications, notifications: s.notifications.map(n => n.id === id ? { ...n, read: true } : n) }
              : { unreadNotifications, appNotifications: s.appNotifications.map(n => n.id === id ? { ...n, read: true } : n) };
          });
        } catch { /* storage unavailable — memory already holds the change */ }

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { error } = source === 'social'
            ? await supabase
                .from('social_notifications')
                .update({ read: true })
                .eq('id', id)
                .eq('user_id', user.id)
            : kind === 'team_message'
              // ⚠️ one team message reaches many people: its read state is a row
              // per READER (mig 0286), never a column on the message
              ? await supabase
                  .from('team_message_reads')
                  .upsert({ message_id: id, user_id: user.id }, { onConflict: 'message_id,user_id', ignoreDuplicates: true })
              : await supabase
                  .from('user_notifications')
                  .update({ read_at: new Date().toISOString() })
                  .eq('id', id)
                  .eq('user_id', user.id)
                  .is('read_at', null);
          if (error) throw error;
        } catch (error) {
          console.error('Error marking notification read:', error);
        }
      },

      markAllNotificationsAsRead: async () => {
        const state = get();
        const hadUnread = Object.keys(state.unreadNotifications).length > 0
          || state.notifications.some(n => !n.read)
          || state.appNotifications.some(n => !n.read);
        // team messages have no per-user row to update — each needs a read row,
        // so collect them BEFORE the unread set is cleared
        const teamIds = [...new Set([
          ...Object.entries(state.unreadNotifications).filter(([, kind]) => kind === 'team_message').map(([id]) => id),
          ...state.appNotifications.filter(n => n.kind === 'team_message' && !n.read).map(n => n.id),
        ])];
        try {
          set(s => ({
            notifications: s.notifications.map(notification => ({
              ...notification,
              read: true
            })),
            appNotifications: s.appNotifications.map(n => ({ ...n, read: true })),
            unreadNotifications: {},
          }));
        } catch { /* storage unavailable — memory already holds the change */ }
        if (!hadUnread) return;

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const [social, app, team] = await Promise.all([
            supabase
              .from('social_notifications')
              .update({ read: true })
              .eq('user_id', user.id)
              .eq('read', false),
            supabase
              .from('user_notifications')
              .update({ read_at: new Date().toISOString() })
              .eq('user_id', user.id)
              .is('read_at', null),
            teamIds.length > 0
              ? supabase
                  .from('team_message_reads')
                  .upsert(teamIds.map(message_id => ({ message_id, user_id: user.id })), { onConflict: 'message_id,user_id', ignoreDuplicates: true })
              : Promise.resolve({ error: null }),
          ]);
          if (social.error) throw social.error;
          if (app.error) throw app.error;
          if (team.error) throw team.error;
        } catch (error) {
          console.error('Error marking notifications read:', error);
        }
      },

      // template Copy has no table write to trigger on — the client writes the
      // notification itself (INSERT policy: sender must be auth.uid())
      notifyTemplateCopied: async (postId, ownerId) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user || user.id === ownerId) return;

          await supabase.from('social_notifications').insert({
            user_id: ownerId,
            type: 'copy',
            sender_id: user.id,
            post_id: postId,
          });
        } catch (error) {
          console.error('Error sending copy notification:', error);
        }
      },

      fetchStories: async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          // RLS scopes to self + followed authors (+ guild stories)
          const { data: rows, error } = await supabase
            .from('social_stories')
            .select('id, user_id, story_type, text_content, media_url, background_color, view_count, reaction_count, created_at, expires_at')
            .in('story_type', ['text', 'image'])
            .gt('expires_at', new Date().toISOString())
            .order('created_at', { ascending: true });

          if (at !== accountGeneration()) return;
          if (error) throw error;
          if (!rows || rows.length === 0) {
            set({ stories: [] });
            return;
          }

          const storyIds = rows.map(r => r.id);

          // author profiles two-step (social_stories.user_id FKs auth.users)
          const [profilesRes, myViewsRes, myReactionsRes] = await Promise.all([
            supabase
              .from('user_profiles')
              .select('id, name, avatar_url, user_level, handle')
              .in('id', [...new Set(rows.map(r => r.user_id))]),
            // needs the R5 viewer-side SELECT policy; [] before it runs
            supabase
              .from('social_story_views')
              .select('story_id')
              .eq('viewer_id', user.id)
              .in('story_id', storyIds),
            supabase
              .from('social_story_reactions')
              .select('story_id, reaction_emoji')
              .eq('user_id', user.id)
              .in('story_id', storyIds),
          ]);

          if (at !== accountGeneration()) return;
          const profiles = new Map((profilesRes.data || []).map(p => [p.id, p]));
          const viewedIds = new Set((myViewsRes.data || []).map(v => v.story_id));
          const myReactions = new Map((myReactionsRes.data || []).map(r => [r.story_id, r.reaction_emoji]));

          set({
            stories: rows.map(row => {
              const profile = profiles.get(row.user_id);
              return {
                id: row.id,
                author: {
                  id: row.user_id,
                  name: profile?.name || 'Unknown User',
                  avatar: profile?.avatar_url || null,
                  level: profile?.user_level || 1,
                  handle: profile?.handle || null,
                },
                text: row.text_content || '',
                mediaUrl: row.media_url || null,
                backgroundColor: row.background_color || '#6d28d9',
                createdAt: row.created_at,
                expiresAt: row.expires_at,
                viewCount: row.view_count || 0,
                reactionCount: row.reaction_count || 0,
                viewedByMe: row.user_id === user.id || viewedIds.has(row.id),
                myReaction: myReactions.get(row.id) || null,
                isMine: row.user_id === user.id,
              };
            }),
          });
        } catch (error) {
          console.error('Error fetching stories:', error);
        }
      },

      addStory: async (text, backgroundColor, mediaUrl) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { error } = await supabase
            .from('social_stories')
            .insert({
              user_id: user.id,
              story_type: mediaUrl ? 'image' : 'text',
              // image stories: text is an optional caption overlay
              text_content: text || null,
              media_url: mediaUrl || null,
              background_color: backgroundColor,
            });
          if (error) throw error;

          await awardSocialXP({ actionType: 'story' });
          await get().fetchStories();
        } catch (error) {
          console.error('Error sharing win:', error);
          throw error;
        }
      },

      markStoryViewed: async (storyId) => {
        const story = get().stories.find(s => s.id === storyId);
        // own stories don't self-view; already-viewed don't re-fire XP
        if (!story || story.isMine || story.viewedByMe) return;

        set(state => ({
          stories: state.stories.map(s =>
            s.id === storyId ? { ...s, viewedByMe: true, viewCount: s.viewCount + 1 } : s
          ),
        }));

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          await supabase
            .from('social_story_views')
            .upsert(
              { story_id: storyId, viewer_id: user.id },
              { onConflict: 'story_id,viewer_id', ignoreDuplicates: true }
            );
          await awardSocialXP({ actionType: 'view', storyId });
        } catch (error) {
          console.error('Error recording story view:', error);
        }
      },

      reactToStory: async (storyId, emoji) => {
        const story = get().stories.find(s => s.id === storyId);
        if (!story) return;

        const removing = story.myReaction === emoji;
        const hadReaction = !!story.myReaction;

        set(state => ({
          stories: state.stories.map(s => {
            if (s.id !== storyId) return s;
            return {
              ...s,
              myReaction: removing ? null : emoji,
              reactionCount: Math.max(0, s.reactionCount + (removing ? -1 : hadReaction ? 0 : 1)),
            };
          }),
        }));

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          // one reaction per story (unique constraint, no UPDATE policy) —
          // changing emoji = delete + insert
          if (hadReaction) {
            const { error } = await supabase
              .from('social_story_reactions')
              .delete()
              .eq('story_id', storyId)
              .eq('user_id', user.id);
            if (error) throw error;
          }
          if (!removing) {
            const { error } = await supabase
              .from('social_story_reactions')
              .insert({ story_id: storyId, user_id: user.id, reaction_emoji: emoji });
            if (error) throw error;

            if (!hadReaction) {
              await awardSocialXP({ actionType: 'storyReact', storyId });
            }
          }
        } catch (error) {
          console.error('Error reacting to story:', error);
        }
      },

      deleteStory: async (storyId) => {
        const story = get().stories.find(s => s.id === storyId);
        if (!story?.isMine) return;

        set(state => ({ stories: state.stories.filter(s => s.id !== storyId) }));

        try {
          const { error } = await supabase
            .from('social_stories')
            .delete()
            .eq('id', storyId);
          if (error) throw error;
        } catch (error) {
          console.error('Error deleting story:', error);
        }
      },

      fetchPacts: async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { data: pactsData, error } = await supabase
            .from('social_pacts')
            .select('*')
            .or(`creator_id.eq.${user.id},partner_id.eq.${user.id}`)
            .order('created_at', { ascending: false });

          if (at !== accountGeneration()) return;
          if (error) throw error;

          const pacts: Pact[] = (pactsData || []).map((pact: any) => ({
            id: pact.id,
            creatorId: pact.creator_id,
            partnerId: pact.partner_id,
            pactType: pact.pact_type,
            actionType: pact.action_type,
            habitId: pact.habit_id,
            questId: pact.quest_id,
            routineId: pact.routine_id,
            customActionName: pact.custom_action_name,
            customActionDescription: pact.custom_action_description,
            creatorRef: parseActionRef(pact.creator_ref),
            partnerRef: parseActionRef(pact.partner_ref),
            creatorActionPayload: parseActionPayload(pact.creator_action_payload),
            cadence: pact.cadence,
            customSchedule: parseCustomSchedule(pact.custom_schedule),
            durationDays: pact.duration_days,
            currentStreak: pact.current_streak,
            longestStreak: pact.longest_streak,
            totalCheckIns: pact.total_check_ins,
            expectedCheckIns: pact.expected_check_ins,
            completionPercentage: pact.completion_percentage,
            status: pact.status,
            creatorAccepted: pact.creator_accepted,
            partnerAccepted: pact.partner_accepted,
            creatorArchived: !!pact.creator_archived,
            partnerArchived: !!pact.partner_archived,
            acceptedAt: pact.accepted_at,
            completedAt: pact.completed_at,
            totalXpEarned: pact.total_xp_earned,
            createdAt: pact.created_at,
            startsAt: pact.started_at,
            endsAt: pact.ends_at,
          }));

          set({ pacts });
        } catch (error) {
          console.error('Error fetching pacts:', error);
        }
      },

      // live status flips for open views — the stale-acceptance fix's second
      // layer (needs the P1 realtime-publication migration; silent before it)
      subscribePacts: async () => {
        const { user } = await syncManager.safeAuthCheck();
        if (!user) return () => {};

        const onChange = () => {
          invalidateActivePactsCache();
          get().fetchPacts();
        };

        // realtime filters can't OR columns — one binding per side
        const channel = supabase
          .channel(`pacts-${user.id}`)
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'social_pacts', filter: `creator_id=eq.${user.id}` },
            onChange
          )
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'social_pacts', filter: `partner_id=eq.${user.id}` },
            onChange
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
      },

      createPact: async (pactData) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return false;

          const row: Record<string, unknown> = {
            creator_id: user.id,
            partner_id: pactData.partnerId,
            pact_type: pactData.pactType,
            action_type: pactData.actionType,
            habit_id: pactData.habitId,
            quest_id: pactData.questId,
            routine_id: pactData.routineId,
            custom_action_name: pactData.customActionName,
            custom_action_description: pactData.customActionDescription,
            cadence: pactData.cadence,
            custom_schedule: pactData.customSchedule ?? null,
            duration_days: pactData.durationDays,
            creator_ref: pactData.creatorRef ?? null,
            creator_action_payload: pactData.creatorActionPayload ?? null,
          };

          let { data: pact, error } = await supabase
            .from('social_pacts')
            .insert(row)
            .select()
            .single();

          // PGRST204 = the P3 ref columns aren't migrated yet — retry legacy
          if (error && error.code === 'PGRST204') {
            delete row.creator_ref;
            delete row.creator_action_payload;
            ({ data: pact, error } = await supabase.from('social_pacts').insert(row).select().single());
          }

          // 42501 = the network-gate policy refused (partner not in network)
          if (error) throw error;

          await awardSocialXP({
            actionType: 'pactCreate',
            pactId: pact.id,
          });

          invalidateActivePactsCache();
          get().fetchPacts();
          return true;
        } catch (error) {
          console.error('Error creating pact:', error);
          return false;
        }
      },

      acceptPact: async (pactId, partnerRef) => {
        try {
          const { error } = await supabase.rpc('respond_pact', {
            p_pact_id: pactId,
            p_accept: true,
            p_partner_ref: partnerRef ?? null,
          });

          // PGRST202 = respond_pact not migrated yet — legacy direct update
          if (error && error.code === 'PGRST202') {
            await supabase
              .from('social_pacts')
              .update({ partner_accepted: true })
              .eq('id', pactId);
          } else if (error) {
            throw error;
          }

          invalidateActivePactsCache();
          get().fetchPacts();
        } catch (error) {
          console.error('Error accepting pact:', error);
        }
      },

      setMyPactRef: async (pactId, ref) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          const pact = get().pacts.find(p => p.id === pactId);
          const side = pactSideOf(pact ?? { creatorId: '', partnerId: '' }, user.id);
          if (!side) return;

          const column = side === 'creator' ? 'creator_ref' : 'partner_ref';
          const { error } = await supabase
            .from('social_pacts')
            .update({ [column]: ref })
            .eq('id', pactId);
          // PGRST204 = ref columns not migrated yet — quietly skip (the
          // name-healing effect retries on a later open)
          if (error && error.code === 'PGRST204') return;
          if (error) throw error;

          invalidateActivePactsCache();
          get().fetchPacts();
        } catch (error) {
          console.error('Error setting pact ref:', error);
        }
      },

      declinePact: async (pactId) => {
        try {
          const { error } = await supabase.rpc('respond_pact', {
            p_pact_id: pactId,
            p_accept: false,
          });

          // PGRST202 = respond_pact not migrated yet — legacy direct update
          if (error && error.code === 'PGRST202') {
            await supabase
              .from('social_pacts')
              .update({
                status: 'cancelled',
                cancelled_at: new Date().toISOString(),
                cancellation_reason: 'declined',
              })
              .eq('id', pactId)
              .eq('status', 'pending');
          } else if (error) {
            throw error;
          }

          invalidateActivePactsCache();
          get().fetchPacts();
        } catch (error) {
          console.error('Error declining pact:', error);
        }
      },

      checkInPact: async (pactId, notes) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { error } = await supabase
            .from('social_pact_check_ins')
            .insert({
              pact_id: pactId,
              user_id: user.id,
              notes,
              completed: true,
            });

          if (error) throw error;

          // social XP is server-side now (P3c pactCheckIn trigger on the
          // check-in row; the daily UNIQUE constraint is the rate limit)
          get().fetchPacts();
        } catch (error) {
          console.error('Error checking in pact:', error);
        }
      },

      cancelPact: async (pactId, reason) => {
        try {
          await supabase
            .from('social_pacts')
            .update({
              status: 'cancelled',
              cancelled_at: new Date().toISOString(),
              cancellation_reason: reason,
            })
            .eq('id', pactId);

          invalidateActivePactsCache();
          get().fetchPacts();
        } catch (error) {
          console.error('Error cancelling pact:', error);
        }
      },

      // client-written notification (no table write to trigger on — the
      // 'copy' precedent); rate-limited to once per cadence period per pact
      nudgePact: async (pactId) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return 'failed';

          const pact = get().pacts.find(p => p.id === pactId);
          if (!pact) return 'failed';
          const mySide = pactSideOf(pact, user.id);
          if (!mySide || !canNudgeSide(pact.pactType, mySide)) return 'failed';

          const stampKey = `metanoia-pact-nudge-${pactId}`;
          try {
            const last = localStorage.getItem(stampKey);
            if (last && isSameCadencePeriod(pact.cadence, new Date(last), new Date())) {
              return 'limited';
            }
          } catch { /* storage unavailable — skip the limit */ }

          const recipient = mySide === 'creator' ? pact.partnerId : pact.creatorId;
          const { error } = await supabase.from('social_notifications').insert({
            user_id: recipient,
            type: 'pact_nudge',
            sender_id: user.id,
          });
          if (error) throw error;

          try { localStorage.setItem(stampKey, new Date().toISOString()); } catch { /* ignore */ }
          return 'sent';
        } catch (error) {
          console.error('Error sending pact nudge:', error);
          return 'failed';
        }
      },

      setPactArchived: async (pactId, archived) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const pact = get().pacts.find(p => p.id === pactId);
          if (!pact) return;
          const side = pactSideOf(pact, user.id);
          if (!side) return;

          // optimistic — the flag only affects the caller's own lists
          const field = side === 'creator' ? 'creatorArchived' : 'partnerArchived';
          set(state => ({
            pacts: state.pacts.map(p => (p.id === pactId ? { ...p, [field]: archived } : p)),
          }));

          const column = side === 'creator' ? 'creator_archived' : 'partner_archived';
          const { error } = await supabase
            .from('social_pacts')
            .update({ [column]: archived })
            .eq('id', pactId);
          if (error) throw error; // PGRST204 before the P1 migration
        } catch (error) {
          console.error('Error archiving pact:', error);
          get().fetchPacts(); // roll back to server truth
        }
      },

      deletePact: async (pactId) => {
        try {
          const { data, error } = await supabase
            .from('social_pacts')
            .delete()
            .eq('id', pactId)
            .select('id');
          if (error) throw error;
          // 0 rows = the no-history policy refused — keep the row visible
          if (!data || data.length === 0) return false;

          set(state => ({ pacts: state.pacts.filter(p => p.id !== pactId) }));
          invalidateActivePactsCache();
          return true;
        } catch (error) {
          console.error('Error deleting pact:', error);
          return false;
        }
      },

      fetchPactCheckIns: async (pactId) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return [];

          const { data: rows, error } = await supabase
            .from('social_pact_check_ins')
            .select('id, pact_id, user_id, check_in_date, notes, created_at')
            .eq('pact_id', pactId)
            .order('created_at', { ascending: false })
            .limit(100);
          if (error) throw error;
          const checkIns = rows || [];
          if (checkIns.length === 0) return [];

          // encouragements (graceful pre-migration: PGRST205 → none)
          const { data: encRows, error: encError } = await supabase
            .from('social_pact_encouragements')
            .select('check_in_id, user_id, emoji')
            .in('check_in_id', checkIns.map(c => c.id));
          const encouragements = encError ? [] : encRows || [];

          // two-step author profiles
          const authorIds = [...new Set(checkIns.map(c => c.user_id))];
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, name, avatar_url, user_level, handle')
            .in('id', authorIds);
          const profileById = new Map(
            (profiles || []).map(p => [
              p.id,
              {
                id: p.id,
                name: p.name || 'Unknown User',
                avatar: p.avatar_url || null,
                level: p.user_level || 1,
                handle: p.handle || null,
              } as SocialProfileRef,
            ])
          );

          return checkIns.map(c => {
            const mine = encouragements.find(e => e.check_in_id === c.id && e.user_id === user.id);
            return {
              id: c.id,
              pactId: c.pact_id,
              userId: c.user_id,
              checkInDate: c.check_in_date,
              notes: c.notes,
              createdAt: c.created_at,
              author: profileById.get(c.user_id) ?? null,
              encouragements: encouragements
                .filter(e => e.check_in_id === c.id)
                .map(e => ({ userId: e.user_id, emoji: e.emoji })),
              myEncouragement: mine?.emoji ?? null,
            };
          });
        } catch (error) {
          console.error('Error fetching pact check-ins:', error);
          return [];
        }
      },

      encourageCheckIn: async (checkInId, emoji, current) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          // no UPDATE policy — change = delete + insert (story-reaction precedent)
          if (current) {
            await supabase
              .from('social_pact_encouragements')
              .delete()
              .eq('check_in_id', checkInId)
              .eq('user_id', user.id);
          }
          if (current !== emoji) {
            const { error } = await supabase
              .from('social_pact_encouragements')
              .insert({ check_in_id: checkInId, user_id: user.id, emoji });
            if (error) throw error;
          }
        } catch (error) {
          console.error('Error encouraging check-in:', error);
        }
      },

      fetchSocialStats: async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { data, error } = await supabase
            .from('domain_stats')
            .select('*')
            .eq('user_id', user.id)
            .eq('domain_id', 'social')
            .maybeSingle();

          if (at !== accountGeneration()) return;
          if (error && error.code !== 'PGRST116') {
            console.error('Error fetching social stats:', error);
            return;
          }

          if (data) {
            set({
              socialStats: {
                userId: data.user_id,
                points: data.points,
                level: data.level,
                socialTitle: data.social_title || 'Observer',
                socialStreakDays: data.social_streak_days || 0,
                lastSocialActivityDate: data.last_social_activity_date,
                socialActionsCount: data.social_actions_count || 0,
              },
            });
          }
        } catch (error) {
          console.error('Error fetching social stats:', error);
        }
      },

      refreshSocialStats: async () => {
        await get().fetchSocialStats();
      },

      // Reputation (SO2): engagement OTHERS gave your content — likes + reposts
      // + replies on your original posts (excludes your reposts). comments_count
      // includes any self-replies on your threads (a known v1 caveat).
      fetchReputationStats: async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { data, error } = await supabase
            .from('social_posts')
            .select('likes_count, repost_count, comments_count')
            .eq('user_id', user.id)
            .is('repost_of_post_id', null);

          if (at !== accountGeneration()) return;
          if (error) {
            console.error('Error fetching reputation stats:', error);
            return;
          }

          const rows = data || [];
          const likes = rows.reduce((s, p) => s + (p.likes_count || 0), 0);
          const reposts = rows.reduce((s, p) => s + (p.repost_count || 0), 0);
          const replies = rows.reduce((s, p) => s + (p.comments_count || 0), 0);
          set({ reputationStats: { likes, reposts, replies, total: likes + reposts + replies } });
        } catch (error) {
          console.error('Error fetching reputation stats:', error);
        }
      },

      // Reputation history (SO2): the per-actor feed behind the aggregate stats —
      // who liked / reposted / replied to your ORIGINAL posts, newest first.
      // Read-only over existing tables; fail-soft (RLS may hide actor identity →
      // 'Someone'; missing tables → empty). No new schema.
      fetchReputationEvents: async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { data: myPosts } = await supabase
            .from('social_posts')
            .select('id')
            .eq('user_id', user.id)
            .is('repost_of_post_id', null)
            .limit(500);
          if (at !== accountGeneration()) return;
          const postIds = (myPosts || []).map((p: any) => p.id);
          if (postIds.length === 0) {
            set({ reputationEvents: [] });
            return;
          }

          const [likesRes, repostsRes, repliesRes] = await Promise.all([
            supabase.from('social_likes').select('id, user_id, created_at').in('post_id', postIds).order('created_at', { ascending: false }).limit(60),
            supabase.from('social_posts').select('id, user_id, created_at').in('repost_of_post_id', postIds).order('created_at', { ascending: false }).limit(60),
            supabase.from('social_comments').select('id, user_id, content, created_at').in('post_id', postIds).order('created_at', { ascending: false }).limit(60),
          ]);

          type Raw = { id: string; user_id: string; created_at: string; content?: string };
          const likes = (likesRes.data || []) as Raw[];
          const reposts = (repostsRes.data || []) as Raw[];
          const replies = (repliesRes.data || []) as Raw[];

          // Skip your own self-engagement (self-replies, self-likes) — this is
          // engagement OTHERS gave you.
          const notMe = (r: Raw) => r.user_id && r.user_id !== user.id;
          const actorIds = [...new Set([...likes, ...reposts, ...replies].filter(notMe).map(r => r.user_id))];

          const profiles = new Map<string, { name: string; avatar: string | null }>();
          if (actorIds.length > 0) {
            const { data: profs } = await supabase.from('user_profiles').select('id, name, avatar_url').in('id', actorIds);
            for (const p of profs || []) profiles.set(p.id, { name: p.name || 'Someone', avatar: p.avatar_url || null });
          }
          if (at !== accountGeneration()) return;
          const actor = (id: string) => profiles.get(id) ?? { name: 'Someone', avatar: null };

          const events: ReputationEvent[] = [
            ...likes.filter(notMe).map(r => ({ id: `like-${r.id}`, kind: 'like' as const, actorName: actor(r.user_id).name, actorAvatar: actor(r.user_id).avatar, createdAt: r.created_at })),
            ...reposts.filter(notMe).map(r => ({ id: `repost-${r.id}`, kind: 'repost' as const, actorName: actor(r.user_id).name, actorAvatar: actor(r.user_id).avatar, createdAt: r.created_at })),
            ...replies.filter(notMe).map(r => ({ id: `reply-${r.id}`, kind: 'reply' as const, actorName: actor(r.user_id).name, actorAvatar: actor(r.user_id).avatar, content: r.content, createdAt: r.created_at })),
          ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 80);

          set({ reputationEvents: events });
        } catch (error) {
          console.error('Error fetching reputation events:', error);
        }
      },

      // Contribution feeder (SO7): how many times others copied my shared items.
      // notifyTemplateCopied writes a 'copy' notification to the sharer per copy.
      fetchCopiesReceived: async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { count, error } = await supabase
            .from('social_notifications')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('type', 'copy');

          if (at !== accountGeneration()) return;
          if (error) {
            console.error('Error fetching copies received:', error);
            return;
          }
          set({ copiesReceived: count || 0 });
        } catch (error) {
          console.error('Error fetching copies received:', error);
        }
      },
    }),
    {
      name: 'social-storage',
      version: 3,
      storage: accountStorage('claim'),
    }
  )
);

scopeToAccount(useSocialStore);
